import { useEffect, useState } from "preact/hooks";

import { detectRegime } from "./geo";
import {
  type ConsentDecision,
  type ConsentRecord,
  type ConsentRegime,
  loadConsent,
  saveConsent,
} from "./state";

// Banner copy per regime. Kept terse — Phase 4 adds granular toggles. The
// "decline" path doesn't disable the feed; it just locks down the
// data-collection surface that Phase 4 introduces.
const COPY: Record<ConsentRegime, { headline: string; body: string }> = {
  gdpr: {
    headline: "Your privacy choices (GDPR)",
    body: "We store a tiny manifest of headlines on your device for offline use. Nothing leaves your browser unless you accept. You can change your mind anytime.",
  },
  lgpd: {
    headline: "Suas escolhas de privacidade (LGPD)",
    body: "Armazenamos um pequeno manifesto de manchetes no seu dispositivo para uso offline. Nada sai do seu navegador a menos que você aceite.",
  },
  ccpa: {
    headline: "Your California privacy rights",
    body: "We do not sell or share your personal information. We cache headlines locally on your device for offline reading.",
  },
  "us-state": {
    headline: "Your state privacy rights",
    body: "We do not sell or share your personal information. We cache headlines locally on your device for offline reading.",
  },
  "us-default": {
    headline: "Local-only by default",
    body: "Headlines are cached on your device so the app works offline. Nothing is reported to us in this version.",
  },
  default: {
    headline: "Local-only by default",
    body: "Headlines are cached on your device so the app works offline. Nothing is reported to us in this version.",
  },
};

export interface ConsentBannerProps {
  onResolved: (record: ConsentRecord) => void;
}

export function ConsentBanner({ onResolved }: ConsentBannerProps) {
  const [regime, setRegime] = useState<ConsentRegime | null>(null);

  useEffect(() => {
    let cancelled = false;
    detectRegime().then((r) => {
      if (!cancelled) setRegime(r);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!regime) return null;

  const copy = COPY[regime];
  const decide = (decision: ConsentDecision) => {
    const record: ConsentRecord = {
      regime,
      decision,
      decided_at: Date.now(),
    };
    saveConsent(record);
    onResolved(record);
  };

  return (
    <div class="consent-banner" role="dialog" aria-labelledby="consent-headline">
      <h2 id="consent-headline">{copy.headline}</h2>
      <p>{copy.body}</p>
      <div class="consent-actions">
        <button class="primary" onClick={() => decide("accepted")}>
          Accept
        </button>
        <button onClick={() => decide("declined")}>Decline</button>
      </div>
      <p class="consent-footnote">
        Regime: <code>{regime}</code>. Phase 3 collects nothing on the
        server — see the privacy notice for details.
      </p>
    </div>
  );
}

export { loadConsent };
